import { FormikValues } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';

import { Card } from '@material-ui/core';
import LocalLibraryIcon from '@material-ui/icons/LocalLibrary';

import AppSnackbar from '../../../common/AppSnackbar';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineSelect from '../../../common/inline/InlineSelect';
import InlineTextField from '../../../common/inline/InlineTextField';
import { FormikSelectOptions } from '../../../common/formik/FormikSelectField';
import { TaskType } from '../../task-types/TaskType.interface';
import { useFindTaskTypes } from '../../task-types/TaskType.hooks';
import { useUpdateWorkflow, useFindWorkflows } from '../Workflow.hooks';
import { WorkflowDto, WorkflowStatus } from '../Workflow.interface';

const validationSchema = Yup.object({
  name: Yup.string().required('Required')
});
export interface WorkflowInfoCardProps {
  workflow: WorkflowDto;
  onChange?: (workflow: WorkflowDto) => void;
}

const getInitialValues = (workflow?: Partial<WorkflowDto>) => {
  return {
    name: workflow?.name || '',
    taskType: workflow?.taskType,
    description: workflow?.description || ''
  };
};

const WorkflowInfoCard: React.FC<WorkflowInfoCardProps> = ({
  workflow,
  onChange
}) => {
  const formikRef = useRef<FormikValues>();
  const {
    updatedWorkflow,
    isUpdating,
    updateError,
    updateWorkflow
  } = useUpdateWorkflow(workflow.id);
  const [{ data: taskTypes }, findTaskTypes] = useFindTaskTypes();

  const options: FormikSelectOptions[] = useMemo(() => {
    return [
      { label: 'Any', value: '' },
      ...(taskTypes || []).map((taskType: TaskType) => ({
        label: taskType.label,
        value: taskType.name
      }))
    ];
  }, [taskTypes]);

  // find conflicting workflows for specific entity
  const { workflowList, isLoading, findWorkflows } = useFindWorkflows();
  const workflowListRef = useRef(workflowList);

  const [initialValues, setInitialValues] = useState<Partial<WorkflowDto>>();
  const [updateWorkflowDto, setUpdateWorkflowDto] = useState<
    Partial<WorkflowDto>
  >();
  const [openEntityConfirm, setOpenEntityConfirm] = useState(false);

  const isPublished: boolean = useMemo(() => {
    return workflow.status === WorkflowStatus.Published;
  }, [workflow]);

  useEffect(() => {
    setInitialValues(getInitialValues(workflow));
  }, [workflow]);

  useEffect(() => {
    findTaskTypes();
  }, [findTaskTypes]);

  useEffect(() => {
    // notify parent component of updated workflowDto
    if (updatedWorkflow) {
      onChange?.(updatedWorkflow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedWorkflow]);

  useEffect(() => {
    // check if conflicting workflows for entity
    if (
      workflowList &&
      workflowListRef.current !== workflowList &&
      !isLoading &&
      !isUpdating
    ) {
      if (
        workflowList.data.length > 0 &&
        workflowList.data[0].id !== workflow.id
      ) {
        workflowListRef.current = workflowList;
        setOpenEntityConfirm(true);
      } else if (workflowList.data.length === 0 && updateWorkflowDto) {
        // if no conflict then update
        updateWorkflow(updateWorkflowDto);
        workflowListRef.current = workflowList;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateWorkflow, updateWorkflowDto, workflow.id, workflowList]);

  return (
    <Card>
      <CardTitle>
        <LocalLibraryIcon />
        Workflow Info
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={initialValues}
          validationSchema={validationSchema}
          isSubmitting={isUpdating || isLoading}
          error={updateError}
          onSubmit={(workflowDto: Partial<WorkflowDto>) => {
            console.log('workflowDto:', workflowDto);
            console.log('initialValues:', initialValues);
            const { taskType } = workflowDto;
            // update if no taskType changes
            if (!taskType || initialValues?.taskType?.name === taskType.name) {
              updateWorkflow(workflowDto);
              return;
            }
            // skip if there are no loaded taskTypes
            if (!taskTypes || taskTypes.length === 0) {
              return;
            }
            workflowDto.taskType = taskTypes.find(
              (t: TaskType) => t.name === taskType.name
            );
            // allow unset for taskType
            if (!workflowDto.taskType) {
              workflowDto.taskTypeName = null;
            }
            setUpdateWorkflowDto(workflowDto);
            findWorkflows({ taskType: taskType.name });
          }}
        >
          <InlineForm>
            <InlineTextField
              name='name'
              label='Name'
              placeholder='Provide a name for the workflow.'
              disabled={isPublished}
            />
            <InlineSelect
              name='taskType.name'
              label='Task Type'
              options={options}
              disabled={isPublished}
            />
            <InlineTextField
              multiline
              name='description'
              label='Description'
              placeholder='Provide an overview of the workflow.'
              disabled={isPublished}
            />
          </InlineForm>
        </InlineFormik>
        <AppSnackbar severity='error' open={Boolean(updateError)}>
          Error occurred: {updateError?.message}
        </AppSnackbar>
      </CardContent>
      {updateWorkflowDto && openEntityConfirm && (
        <ConfirmDialog
          title='Override Entity Workflow'
          message={`This will replace "${workflowList?.data[0]?.name}" as the default workflow for entity "${updateWorkflowDto.taskType?.label}".`}
          open={openEntityConfirm}
          onCancel={() => {
            setOpenEntityConfirm(false);
            console.log('initialValues?.taskType', initialValues?.taskType);
            setUpdateWorkflowDto({ taskType: initialValues?.taskType });
          }}
          onConfirm={() => {
            setOpenEntityConfirm(false);
            updateWorkflow(updateWorkflowDto);
          }}
        />
      )}
    </Card>
  );
};

export default WorkflowInfoCard;
